/* @flow */
// typecheck: ignore file
import _ from 'lodash';
import { filter } from 'fuzzaldrin';

import Group from 'entities/Group';
import Groups from 'collections/Groups';
import PotentialChatMember from 'entities/PotentialChatMember';
import PotentialChatMembers from 'collections/PotentialChatMembers';
import { TARGET_TYPES } from 'entities/Message';
import { EVERYONE, STUDENT, PARENT, TEACHER, ADMIN, UNKNOWN, getGroupKey } from 'utils/roleHelpers';

import type { Action } from 'action';
import type { Target } from 'entities/Message';

const fuzzaldrinOptions = {
  key: 'searchString',
};

export const RECIPIENT_TYPES = {
  GROUP_RECIPIENT: 'group_recipient',
  INDIVIDUAL_RECIPIENT: 'individual_recipient',
};

export type Role = {role?: ?string, count?: number};

export type GroupRecipientType = 'group_recipient';
export type IndividualRecipientType = 'individual_recipient';

export type GroupRecipient = {
  type: GroupRecipientType,
  group: Group,
  role: Role,
  id: string,
  searchString?: string,
};

export type IndividualRecipient = {
  type: IndividualRecipientType,
  member: PotentialChatMember,
  id: number,
  searchString?: string,
};

export type Recipient = GroupRecipient | IndividualRecipient;

export type State = {
  recipients: Recipient[], // flat list of all recipients
  results: Array<Recipient[]>, // recipients grouped by class
  resultSelectionIndex: number, // which of a class' results is selected
  selection: Recipient[], // flat list of chosen recipients
  targets: Target[],
  query: string,
  highlightIndex: number,
  loadingIndividualRecipients?: boolean,
};

const initialState: State = {
  recipients: [],
  results: [],
  resultSelectionIndex: 0,
  selection: [],
  targets: [],
  query: '',
  highlightIndex: -1,
};

export const createTargets = (recipients: Recipient[]): Target[] => {
  return recipients.map((r: Recipient) => {
    let t;
    if (r.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
      t = { type: TARGET_TYPES.GROUP_TARGET, group_id: r.group.id, role: r.role.role || EVERYONE };
    } else {
      t = { type: TARGET_TYPES.INDIVIDUAL_TARGET, member_id: r.member.id };
    }
    return t;
  });
};

const rolesOrder = {
  [EVERYONE]: 0,
  [STUDENT]: 1,
  [PARENT]: 2,
  [TEACHER]: 3,
  [ADMIN]: 4,
  [UNKNOWN]: 5,
};

const getRoleOrder = (r: Recipient) => {
  let role = EVERYONE;
  if (r.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
    role = r.role.role || EVERYONE;
  }
  return rolesOrder[role];
};

const groupRecipients = (recipients: GroupRecipient[]) => {
  const resIndex = {};
  recipients.forEach((r: GroupRecipient, i: number) => {
    if (r.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
      if (!resIndex[r.group.id]) {
        resIndex[r.group.id] = i;
      }
    }
  });

  const unsortedRecipients = _(recipients)
    .groupBy(r => r.group.id)
    .values()
    .value();

  return unsortedRecipients
    .sort((a: GroupRecipient[], b: GroupRecipient[]) => resIndex[a[0].group.id] - resIndex[b[0].group.id])
    .map((r: Recipient[]) => {
      return r.sort((a: Recipient, b: Recipient) => getRoleOrder(a) - getRoleOrder(b));
    });
};

const generateResults = (recipients) => {
  const group = recipients.filter(r => r.type === RECIPIENT_TYPES.GROUP_RECIPIENT);
  const individual = recipients.filter(r => r.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT);
  const groupResults = groupRecipients(group);
  const individualResults = _.values(_.groupBy([...individual], r => r.id));
  return [...groupResults, ...individualResults];
};

const calcResults = (recipients, query): Recipient[] => {
  if (query) {
    return filter(recipients, query, fuzzaldrinOptions);
  } else {
    return recipients;
  }
};

const createIndividualRecipient = (member: PotentialChatMember): IndividualRecipient => {
  return {
    type: 'individual_recipient',
    member,
    id: member.id,
    searchString: `${member.getName()} ${member.getFirstName()} ${member.getLastName()} ${member.getNote()} ${member.getGroupNames()}`,
  };
};

const createGroupRecipient = (group: Group, role: Role = {count: group.getMembershipsCount()}) : GroupRecipient => { // eslint-disable-line
  return {
    type: RECIPIENT_TYPES.GROUP_RECIPIENT,
    role,
    group,
    // $FlowIssue
    id: getGroupKey(group.id, role.role),
    searchString: `${group.getName()} ${role.role || EVERYONE}`,
  };
};

type loadGroupsAction = {
  type: 'rbms/LOAD_GROUPS',
  groups: Groups,
};

export const loadGroups = (groups: Groups): loadGroupsAction => ({
  type: 'rbms/LOAD_GROUPS',
  groups,
});

type loadIndividualsAction = {
  type: 'rbms/LOAD_INDIVIDUALS',
  members: PotentialChatMembers,
};

export const loadIndividuals = (members: PotentialChatMembers): loadIndividualsAction => ({
  type: 'rbms/LOAD_INDIVIDUALS',
  members,
});

type changeQueryAction = {
  type: 'rbms/CHANGE_QUERY',
  query: string,
};

export const changeQuery = (query: string): changeQueryAction => ({
  type: 'rbms/CHANGE_QUERY',
  query,
});

type selectAction = {
  type: 'rbms/SELECT',
  recipients: Recipient[],
};

export const select = (recipients: Recipient[]): selectAction => ({
  type: 'rbms/SELECT',
  recipients,
});

type selectInitialTargetsAction = {
  type: 'rbms/SELECT_INITIAL_TARGETS',
  targets: Target[],
};

export const selectInitialTargets = (targets: Target[]): selectInitialTargetsAction => ({
  type: 'rbms/SELECT_INITIAL_TARGETS',
  targets,
});

type selectGroupRecipientAction = {
  group: Group,
  role: Role,
  id: string,
}[]

type selectGroupAction = {
  type: 'rbms/SELECT',
  recipients: selectGroupRecipientAction,
};

export const selectGroup = (group: Group): selectGroupAction => ({
  type: 'rbms/SELECT',
  recipients: [{
    group,
    role: { role: undefined },
    id: `${group.id}~`,
    type: RECIPIENT_TYPES.GROUP_RECIPIENT,
  }],
});

type selectAllGroupsAction = {
  type: 'rbms/SELECT_ALL_GROUPS',
};

export const selectAllGroups = (): selectAllGroupsAction => ({
  type: 'rbms/SELECT_ALL_GROUPS',
});

type deselectGroupAction = {
  type: 'rbms/DESELECT_GROUP',
  group: Group,
};

export const deselectGroup = (group: Group): deselectGroupAction => ({
  type: 'rbms/DESELECT_GROUP',
  group,
});

type deselectIndividualAction = {
  type: 'rbms/DESELECT_INDIVIDUAL',
  member: PotentialChatMember,
};

export const deselectIndividual = (member: PotentialChatMember): deselectIndividualAction => ({
  type: 'rbms/DESELECT_INDIVIDUAL',
  member,
});

type changeResultSelectionAction = {
  type: 'rbms/CHANGE_RESULT_SELECTION',
  selectedIndex: number,
};

export const changeResultSelection = (selectedIndex: number): changeResultSelectionAction => ({
  type: 'rbms/CHANGE_RESULT_SELECTION',
  selectedIndex,
});

type changeHighlightIndexAction = {
  type: 'rbms/CHANGE_HIGHLIGHT_INDEX',
  index: number,
};

export const changeHighlightIndex = (index: number): changeHighlightIndexAction => ({
  type: 'rbms/CHANGE_HIGHLIGHT_INDEX',
  index,
});

type flushAction = {
  type: 'rbms/FLUSH',
};

export const flush = (): flushAction => ({
  type: 'rbms/FLUSH',
});

type resetAction = {
  type: 'rbms/RESET',
};

export const reset = (): resetAction => ({
  type: 'rbms/RESET',
});

export type loadPotentialChatMembersSuccessAction = {
  type: 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST_SUCCESS',
  members: PotentialChatMembers,
};

export const loadPotentialChatMembersSuccess = (members: PotentialChatMembers): loadPotentialChatMembersSuccessAction => ({
  type: 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST_SUCCESS',
  members,
});

export type loadPotentialChatMembersAction = {
  type: 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST',
  query: string,
};

export const loadPotentialChatMembers = (query: string): loadPotentialChatMembersAction => ({
  type: 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST',
  query,
});

export type RbmsAction =
  loadGroupsAction |
  loadIndividualsAction |
  changeQueryAction |
  selectAction |
  selectGroupAction |
  selectAllGroupsAction |
  deselectGroupAction |
  deselectIndividualAction |
  changeResultSelectionAction |
  selectInitialTargetsAction |
  changeHighlightIndexAction |
  resetAction |
  flushAction |
  loadPotentialChatMembersAction |
  loadPotentialChatMembersSuccessAction;

// typecheck: ignore statement
export default (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'rbms/LOAD_GROUPS': {
      const { groups } = action;
      const recipients = groups.reduce((acc, group: Group) => {
        const postableRoles = group.getPostableRoles();
        const recipientsToInclude = group.getRoles()
          .filter(role => postableRoles.includes(role.role) || role.role === UNKNOWN)
          .map(role => createGroupRecipient(group, role));
        if (postableRoles.includes(EVERYONE)) {
          recipientsToInclude.push(createGroupRecipient(group));
        }
        return [...acc, ...recipientsToInclude];
      }, []);
      const { recipients: currentRecipients, ...rest } = state;
      const allRecipients = _.uniqBy([...currentRecipients, ...recipients], r => r.id);

      return {
        ...rest,
        results: generateResults(calcResults(allRecipients, state.query)),
        recipients: allRecipients,
      };
    }
    case 'rbms/LOAD_INDIVIDUALS': {
      const { members } = action;
      const recipients = members.map(m => createIndividualRecipient(m));
      const { recipients: currentRecipients, ...rest } = state;
      const allRecipients = _.uniqBy([...currentRecipients, ...recipients], r => r.id);

      return {
        ...rest,
        results: generateResults(calcResults(allRecipients, state.query)),
        recipients: allRecipients,
      };
    }

    case 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST_SUCCESS': {
      const { members } = action;
      const { recipients: currentRecipients, query } = state;
      let allRecipients = [...currentRecipients];
      if (members) {
        const recipients = members.map(m => createIndividualRecipient(m));
        allRecipients = [...allRecipients, ...recipients];
      }
      allRecipients = _.uniqBy(allRecipients, r => r.id);

      return {
        ...state,
        results: generateResults(calcResults(allRecipients, query)),
        recipients: allRecipients,
        loadingIndividualRecipients: false,
        query,
      };
    }

    case 'rbms/CHANGE_QUERY': {
      const { query } = action;
      return {
        ...state,
        results: generateResults(calcResults(state.recipients, query)),
        resultSelectionIndex: 0,
        query,
      };
    }
    case 'rbms/SELECT': {
      const recipient: Recipient = action.recipients.length === 1 ?
        action.recipients[0] :
        action.recipients[state.resultSelectionIndex];

      const { selection } = state;
      let newSelection;
      if (state.selection.some(s => s.id === recipient.id)) {
        // selecting something already selected removes it
        newSelection = selection.filter(s => s.id !== recipient.id);
      } else {
        if (recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
          if (recipient.role.role === undefined) {
            // when adding everyone, remove all the others from that group
            const filteredSelection = selection.filter(s => !s.group || s.group.id !== recipient.group.id);
            newSelection = [...filteredSelection, recipient];
          } else {
            // when adding !everyone, remove everyone
            const selectionWithoutEveryone = selection.filter(s => {
              if (s.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT) {
                return true;
              } else {
                return !(s.group.id === recipient.group.id && s.role.role === undefined);
              }
            });
            newSelection = [...selectionWithoutEveryone, recipient];
          }
        } else {
          newSelection = [...selection, recipient];
        }
      }
      return {
        ...state,
        selection: newSelection,
        targets: createTargets(newSelection),
      };
    }
    case 'rbms/DESELECT_GROUP': {
      const { group } = action;
      const filteredSelection = state.selection.filter(s => {
        if (s.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT) {
          return true;
        }
        return s.group.id !== group.id;
      });
      return {
        ...state,
        selection: filteredSelection,
        targets: createTargets(filteredSelection),
      };
    }
    case 'rbms/DESELECT_INDIVIDUAL': {
      const { member } = action;
      const filteredSelection = state.selection.filter(s => {
        if (s.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
          return true;
        }
        return s.id !== member.id;
      });
      return {
        ...state,
        selection: filteredSelection,
        targets: createTargets(filteredSelection),
      };
    }
    case 'rbms/SELECT_INITIAL_TARGETS': {
      const { targets } = action;
      const selection = targets.map(t => {
        return state.recipients.find(r => r.id === getGroupKey(t.group_id, t.role));
      });
      return {
        ...state,
        selection,
        targets: createTargets(selection),
      };
    }
    case 'rbms/SELECT_ALL_GROUPS': {
      const newSelection = state.recipients.filter(r => {
        if (r.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT) {
          return true;
        }
        return r.role.role === undefined;
      });

      const allRecipients = _.uniqBy(newSelection, r => r.id);

      return {
        ...state,
        selection: allRecipients,
        targets: createTargets(allRecipients),
      };
    }
    case 'rbms/CHANGE_RESULT_SELECTION': {
      return {
        ...state,
        resultSelectionIndex: action.selectedIndex,
      };
    }
    case 'rbms/CHANGE_HIGHLIGHT_INDEX': {
      return {
        ...state,
        highlightIndex: action.index,
      };
    }
    case 'rbms/RESET': {
      return {
        ...initialState,
        results: generateResults(calcResults(state.recipients, '')),
        recipients: state.recipients,
      };
    }
    case 'rbms/FLUSH': {
      return initialState;
    }

    case 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST': {
      return {
        ...state,
        query: action.query,
        loadingIndividualRecipients: true,
      };
    }
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/reducers/roleBasedMessagingSearch.js